// src/pages/Results.jsx - Redesigned with Modern Orange-to-Pink Theme
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SearchNavigation from '../components/SearchNavigation';
import ApiService from '../services/apiService';

// Searchable Dropdown Component
const SearchableDropdown = ({
  items,
  selectedItem,
  onSelect,
  isOpen,
  onToggle,
  placeholder,
  icon,
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredItems = items.filter(item =>
    item.toString().toLowerCase().includes(searchTerm.toLowerCase())
  );

  React.useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const handleItemSelect = (item) => {
    onSelect(item);
    setSearchTerm('');
    onToggle(false);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter' && filteredItems.length > 0) {
      handleItemSelect(filteredItems[0]);
    }
  };

  return (
    <div className={`modern-dropdown-container ${className}`}>
      <button
        className="modern-dropdown-btn"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle(!isOpen);
        }}
      >
        {icon && <i className={`${icon}`}></i>}
        <span>{selectedItem}</span>
        <i className={`bx bx-chevron-down ${isOpen ? 'rotated' : ''}`}></i>
      </button>

      {isOpen && (
        <div className="modern-dropdown-menu">
          <div className="dropdown-search-box">
            <input
              type="text"
              className="dropdown-search"
              placeholder={`Search ${placeholder?.toLowerCase()}...`}
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
            <i className="bx bx-search search-icon-dropdown"></i>
          </div>

          <div className="dropdown-items-wrapper">
            {filteredItems.length === 0 ? (
              <div className="no-items-found">No results found</div>
            ) : (
              filteredItems.map((item, index) => (
                <button
                  key={`${item}-${index}`}
                  className={`dropdown-item-btn ${item === selectedItem ? 'selected' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleItemSelect(item);
                  }}
                >
                  {item}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Results = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all-courts');
  const [allResults, setAllResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [displayedResults, setDisplayedResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalResults, setTotalResults] = useState(0);
  const [searchType, setSearchType] = useState('Search');
  const [isLoading, setIsLoading] = useState(true);
  const [embeddingVector, setEmbeddingVector] = useState(null);
  const [searchData, setSearchData] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);

  // Filter states
  const [refineText, setRefineText] = useState('');
  const [selectedYear, setSelectedYear] = useState('All Years');
  const [selectedCourt, setSelectedCourt] = useState('All Courts');
  const [availableYears, setAvailableYears] = useState(['All Years']);
  const [availableCourts, setAvailableCourts] = useState(['All Courts']);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showCourtDropdown, setShowCourtDropdown] = useState(false);

  // API response data
  const [apiCourtsList, setApiCourtsList] = useState([]);
  const [apiYearsList, setApiYearsList] = useState([]);
  const [openSortMenuId, setOpenSortMenuId] = useState(false);
  const [sortMode, setSortMode] = useState("");
  const [isAi, setIsAi] = useState(false);
  const [showAddBookmarkModal, setShowAddBookmarkModal] = useState(false);
  const [bookmarkName, setBookmarkName] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleSort = (item) => {
    setSortMode(item);
    setOpenSortMenuId(false);
    handlePageChange(1, item);
  };

  const toggleSortMenu = () => {
    setOpenSortMenuId(!openSortMenuId);
  };

  useEffect(() => {
    const savedResults = localStorage.getItem('searchResults');

    if (savedResults) {
      try {
        const resultsData = JSON.parse(savedResults);
        const results = resultsData.results || [];
        setAllResults(results);
        setFilteredResults(results);
        setDisplayedResults(results);
        setSearchQuery(resultsData.query || '');
        setTotalResults(resultsData.totalCount || results.length);
        setSearchType(resultsData.searchType || 'Search');
        setEmbeddingVector(resultsData.embeddingVector || null);
        setSearchData(resultsData.searchData || null);

        extractFiltersFromApiResponse(resultsData);
      } catch (error) {
        console.error('Failed to parse search results:', error);
        setAllResults([]);
        setFilteredResults([]);
        setTotalResults(0);
      }
    } else {
      navigate('/ai-search');
    }

    setIsLoading(false);
  }, [navigate]);

  const handleAddBookmark = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const judgmentData = displayedResults.find(result => result.id === currentIndex);
      const bookmarkData = {
        UserId: 'ok',
        appelantRespondant: judgmentData.appellant + ' Vs. ' + judgmentData.respondent,
        highlightLink: judgmentData.id,
        jDate: judgmentData.date,
        bokmarkName: bookmarkName
      };

      const result = await ApiService.addBookmark(bookmarkData);
      alert(result);
    } catch (error) {
      console.error('❌ error:', error);
      alert(error);
    } finally {
      setIsLoading(false);
      setShowAddBookmarkModal(false);
    }
  };

  const extractFiltersFromApiResponse = (resultsData) => {
    setIsAi(resultsData.searchType == "AI Search");

    if (resultsData.yearList && Array.isArray(resultsData.yearList)) {
      const years = resultsData.yearList
        .map(item => resultsData.searchType == "AI Search" ? item.key : `${item.key} (${item.count})`)
        .sort((a, b) => b - a);
      setApiYearsList(resultsData.yearList);
      setAvailableYears(['All Years', ...years]);
    } else {
      extractFiltersFromResults(resultsData.results || []);
    }

    if (resultsData.courtsList && Array.isArray(resultsData.courtsList)) {
      const data = localStorage.getItem('userp');
      var courtMap = JSON.parse(data)?.courts;

      const merged = resultsData.courtsList
        .map(k => {
          const match = courtMap.find(p => p.key === k.key);
          return match ? { ...k, value: match.value } : null;
        })
        .filter(Boolean);

      merged.sort((a, b) => a.value.localeCompare(b.value));
      const courtNames = merged.map(item => `${item.value} (${item.count})`);

      setApiCourtsList(resultsData.courtsList);
      setAvailableCourts(['All Courts', ...courtNames]);
    } else {
      extractFiltersFromResults(resultsData.results || []);
    }
  };

  const extractFiltersFromResults = (results) => {
    const years = new Set();
    const courts = new Set();

    results.forEach(result => {
      if (result.date) {
        try {
          const dateStr = result.date.toString();
          let year = null;

          if (dateStr.length === 8) {
            year = dateStr.substring(0, 4);
          } else if (dateStr.includes('/')) {
            const parts = dateStr.split('/');
            if (parts.length === 3) {
              year = parts[2];
            }
          } else if (dateStr.includes('-')) {
            year = dateStr.split('-')[0];
          }

          if (year && year.length === 4 && !isNaN(year)) {
            years.add(year);
          }
        } catch (error) {
          console.warn('Date parsing error:', error);
        }
      }

      if (result.court && typeof result.court === 'string') {
        const cleanCourt = result.court.trim();
        if (cleanCourt) {
          const normalizedCourt = cleanCourt.toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          courts.add(normalizedCourt);
        }
      }
    });

    setAvailableYears(['All Years', ...Array.from(years).sort((a, b) => b - a)]);
    setAvailableCourts(['All Courts', ...Array.from(courts).sort()]);
  };

  useEffect(() => {
    const hasFilters = refineText.trim() || selectedYear !== 'All Years' || selectedCourt !== 'All Courts';

    if (hasFilters) {
      if (currentPage > 1) {
        setCurrentPage(1);
        return;
      } else {
        handlePageChange(1);
      }
    } else {
      handlePageChange(1);
    }
  }, [selectedYear, selectedCourt]);

  useEffect(() => {
    const hasFilters = refineText.trim() || selectedYear !== 'All Years' || selectedCourt !== 'All Courts';
    if (hasFilters) {
      const total = Math.ceil(totalResults / resultsPerPage);
      setTotalPages(total > 400 ? 400 : total);
    } else {
      const total = Math.ceil(totalResults / resultsPerPage);
      setTotalPages(total > 400 ? 400 : total);
    }
  }, [filteredResults, currentPage, resultsPerPage, totalResults]);

  const handlePageChange = async (page, sortm) => {
    if (page >= 1 && !isLoading) {
      setIsLoading(true);

      try {
        const hasFilters = refineText.trim() || selectedYear !== 'All Years' || selectedCourt !== 'All Courts';
        var payload = JSON.parse(localStorage.getItem('searchPayload'));
        payload.page = page;

        if (selectedYear && selectedYear !== 'All Years') {
          payload.requests[0].yearFrom = selectedYear.split('(')[0].trim();
          payload.requests[0].yearTo = selectedYear.split('(')[0].trim();
        }

        if (selectedCourt && selectedCourt !== 'All Courts') {
          const data = localStorage.getItem('userp');
          var courtMap = JSON.parse(data)?.courts;
          const match = courtMap.find(p => p.value === selectedCourt.split('(')[0].trim());
          payload.requests[0].mainkeys = [match.key];
        }

        if (!sortm && sortMode != "") {
          payload.sortBy = sortMode === 'most-relevant' ? 'rele' :
            sortMode === 'most-recent' ? 'year' :
              sortMode === 'most-referred' ? 'rele' : 'year';
          payload.sortOrder = sortMode === 'oldest' ? 'asc' : 'desc';
        }

        if (sortm) {
          payload.sortBy = sortm === 'most-relevant' ? 'rele' :
            sortm === 'most-recent' ? 'year' :
              sortm === 'most-referred' ? 'rele' : 'year';
          payload.sortOrder = sortm === 'oldest' ? 'asc' : 'desc';
        }

        if (refineText.trim()) {
          payload.requests.push({ query: refineText.trim() });
        }

        const apiResponse = await ApiService.executeSearchFilter(payload);
        setTotalResults(apiResponse.total);
        const total = Math.ceil(apiResponse.total / resultsPerPage);
        setTotalPages(total > 400 ? 400 : total);
        setDisplayedResults(apiResponse.hits || []);
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (error) {
        console.error('Pagination failed:', error);
        const startIndex = (page - 1) * resultsPerPage;
        const endIndex = startIndex + resultsPerPage;
        setDisplayedResults(allResults.slice(startIndex, endIndex));
        setCurrentPage(page);
      }

      setIsLoading(false);
    }
  };

  const handleRefineSearch = () => {
    setCurrentPage(1);
    handlePageChange(1);
  };

  const handleClearFilters = () => {
    setRefineText('');
    setSelectedYear('All Years');
    setSelectedCourt('All Courts');
    setCurrentPage(1);
  };

  const generatePaginationButtons = () => {
    const buttons = [];
    const maxVisible = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          className={`pagination-number ${i === currentPage ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return buttons;
  };

  const formatResultContent = (result) => {
    let content = '';

    if (result.headnoteAll) {
      content = result.headnoteAll;
    } else if (result.newHeadnote) {
      content = result.newHeadnote;
    } else if (result.issueForConsideration) {
      content = result.issueForConsideration;
    } else if (result.lawPoint) {
      content = result.lawPoint;
    } else if (result.held) {
      content = result.held;
    } else {
      content = 'Content not available';
    }

    return content.length > 800 ? content.substring(0, 800) + '...' : content;
  };

  const formatResultTitle = (result) => {
    if (result.appellant && result.respondent) {
      return `${result.appellant} vs ${result.respondent}`;
    }
    if (result.id) {
      return `Case ${result.id}`;
    }
    return 'Legal Case';
  };

  const formatCourt = (result) => {
    return result.court || 'Court not specified';
  };

  const formatDate = (result) => {
    if (result.date) {
      try {
        const dateStr = result.date.toString();
        if (dateStr.length === 8) {
          const year = dateStr.substring(0, 4);
          const month = dateStr.substring(4, 6);
          const day = dateStr.substring(6, 8);
          return `${day}/${month}/${year}`;
        }
        return result.date;
      } catch {
        return result.date;
      }
    }
    return 'Date not available';
  };

  const handleJudgementClick = (keycode, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (keycode) {
      const currentIndex = displayedResults.findIndex(result => result.id === keycode);
      const navigationData = {
        currentIndex: currentIndex,
        results: displayedResults.map(result => ({
          keycode: result.id,
          title: formatResultTitle(result),
          court: result.court,
          date: result.date
        })),
        searchQuery: searchQuery,
        totalResults: totalResults,
        currentPage: currentPage
      };

      sessionStorage.setItem('judgementNavigation', JSON.stringify(navigationData));
      navigate(`/judgement/${keycode}`);
    }
  };

  if (isLoading) {
    return (
      <div className="results-layout">
        <Navbar />
        <SearchNavigation />
        <div className="loading-fullscreen-results">
          <i className="bx bx-loader-alt bx-spin"></i>
          <p>Loading search results...</p>
        </div>
        <style>{`
          .results-layout { min-height: 100vh; background: #fafafa; }
          .loading-fullscreen-results {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            gap: 15px;
          }
          .loading-fullscreen-results i { font-size: 60px; color: #E91E63; }
          .loading-fullscreen-results p { font-size: 18px; color: #666; margin: 0; }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .bx-spin { animation: spin 1s linear infinite; }
        `}</style>
      </div>
    );
  }

  const hasFilters = refineText.trim() || selectedYear !== 'All Years' || selectedCourt !== 'All Courts';
  const currentDisplayCount = totalResults;

  return (
    <div className="results-layout">
      <Navbar />
      <SearchNavigation />

      <div className="results-page-wrapper">
        {/* Hero Section */}
        <div className="results-hero">
          <div className="hero-content-results">
            <div className="search-type-badge">
              <i className="bx bx-search-alt-2"></i>
              <span>{searchType}</span>
            </div>
            <h1 className="results-hero-title">{searchQuery || 'Search Results'}</h1>
            <p className="results-hero-subtitle">
              Found {currentDisplayCount.toLocaleString()} relevant case{currentDisplayCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        <div className="results-main-container">
          {/* Filters & Pagination Card */}
          <div className="filters-card">
            {/* Top Row - Pagination */}
            <div className="pagination-section">
              <div className="pagination-info">
                <i className="bx bx-file"></i>
                <span>
                  Showing {((currentPage - 1) * resultsPerPage) + 1} - {Math.min(currentPage * resultsPerPage, currentDisplayCount)} of {currentDisplayCount}
                </span>
              </div>

              <div className="pagination-controls">
                {currentPage > 1 && (
                  <button className="pagination-btn" onClick={() => handlePageChange(currentPage - 1)}>
                    <i className="bx bx-chevron-left"></i>
                    Previous
                  </button>
                )}

                {generatePaginationButtons()}

                {currentPage < totalPages && (
                  <button className="pagination-btn" onClick={() => handlePageChange(currentPage + 1)}>
                    Next
                    <i className="bx bx-chevron-right"></i>
                  </button>
                )}

                {totalPages > 1 && currentPage < totalPages && (
                  <button className="pagination-btn last-btn" onClick={() => handlePageChange(totalPages)}>
                    Last
                  </button>
                )}
              </div>
            </div>

            {/* Bottom Row - Filters */}
            <div className="filters-section">
              <div className="search-refine-box">
                <i className="bx bx-search"></i>
                <input
                  type="text"
                  placeholder="Refine search in results..."
                  className="refine-input"
                  value={refineText}
                  onChange={(e) => setRefineText(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleRefineSearch()}
                />
                {refineText && (
                  <button className="clear-refine" onClick={() => setRefineText('')}>
                    <i className="bx bx-x"></i>
                  </button>
                )}
              </div>

              <button className="refine-search-btn" onClick={handleRefineSearch}>
                <i className="bx bx-search-alt"></i>
                Refine
              </button>

              <SearchableDropdown
                items={availableYears}
                selectedItem={selectedYear}
                onSelect={(year) => {
                  setSelectedYear(year);
                  setShowCourtDropdown(false);
                }}
                isOpen={showYearDropdown}
                onToggle={(isOpen) => {
                  setShowYearDropdown(isOpen);
                  if (isOpen) setShowCourtDropdown(false);
                }}
                placeholder="years"
                icon="bx bx-calendar"
              />

              <SearchableDropdown
                items={availableCourts}
                selectedItem={selectedCourt}
                onSelect={(court) => {
                  setSelectedCourt(court);
                  setShowYearDropdown(false);
                }}
                isOpen={showCourtDropdown}
                onToggle={(isOpen) => {
                  setShowCourtDropdown(isOpen);
                  if (isOpen) setShowYearDropdown(false);
                }}
                placeholder="courts"
                icon="bx bx-building"
              />

              <div className="filter-dropdown-wrapper">
                <button 
                  className="modern-dropdown-btn" 
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSortMenu();
                  }}
                  disabled={isAi}
                >
                  <i className="bx bx-sort-alt-2"></i>
                  <span>Sort Results</span>
                  <i className={`bx bx-chevron-down ${openSortMenuId ? 'rotated' : ''}`}></i>
                </button>

                {openSortMenuId && (
                  <div className="modern-dropdown-menu sort-menu">
                    <button onClick={() => handleSort("most-relevant")} className="dropdown-item-btn">
                      <i className="bx bx-star"></i>
                      Most Relevant
                    </button>
                    <button onClick={() => handleSort("most-recent")} className="dropdown-item-btn">
                      <i className="bx bx-time"></i>
                      Most Recent
                    </button>
                    <button onClick={() => handleSort("oldest")} className="dropdown-item-btn">
                      <i className="bx bx-history"></i>
                      Oldest
                    </button>
                  </div>
                )}
              </div>

              {hasFilters && (
                <button className="clear-filters-btn" onClick={handleClearFilters}>
                  <i className="bx bx-x-circle"></i>
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Results Grid */}
          <div className="results-grid">
            {displayedResults.length === 0 ? (
              <div className="no-results-found">
                <i className="bx bx-search-alt"></i>
                <h3>No Results Found</h3>
                <p>Try adjusting your search filters or refine your query</p>
                <button className="retry-btn" onClick={handleClearFilters}>
                  Clear All Filters
                </button>
              </div>
            ) : (
              displayedResults.map((result, index) => (
                <div key={result.id || index} className="result-card-modern">
                  <div className="result-card-header">
                    <div className="result-number">
                      #{((currentPage - 1) * resultsPerPage) + index + 1}
                    </div>
                    {/* {result.accuracyPercentage > 0 && (
                      <div className="accuracy-badge-modern">
                        <i className="bx bx-target-lock"></i>
                        {(result.accuracyPercentage || 0).toFixed(1)}% Match
                      </div>
                    )} */}
                  </div>

                  <h3
                    className="result-title-modern"
                    onClick={(e) => handleJudgementClick(result.id, e)}
                  >
                    {formatResultTitle(result)}
                  </h3>

                  <div className="result-meta-modern">
                    <div className="meta-item-modern">
                      <i className="bx bx-buildings"></i>
                      <span>{formatCourt(result)}</span>
                    </div>
                    <div className="meta-item-modern">
                      <i className="bx bx-calendar-alt"></i>
                      <span>{formatDate(result)}</span>
                    </div>
                    {result.fullequivicit && (
                      <div className="meta-item-modern citation-item">
                        <i className="bx bx-book-bookmark"></i>
                        <span>{result.fullequivicit}</span>
                      </div>
                    )}
                  </div>

                  <div
                    className="result-content-modern"
                    dangerouslySetInnerHTML={{ __html: formatResultContent(result) }}
                  />

                  {result.id && (
                    <div className="result-actions-modern">
                      <button
                        className="action-primary-btn"
                        onClick={(e) => handleJudgementClick(result.id, e)}
                      >
                        <i className="bx bx-book-open"></i>
                        Read Full Judgment
                      </button>

                      <div className="action-secondary-btns">
                        <button
                          className="action-icon-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAddBookmarkModal(true);
                            setCurrentIndex(result.id);
                          }}
                          title="Bookmark"
                        >
                          <i className="bx bx-bookmark"></i>
                        </button>

                        <button
                          className="action-icon-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            const printContent = `
                              <html>
                                <head>
                                  <title>${formatResultTitle(result)}</title>
                                  <style>
                                    body { font-family: Arial, sans-serif; padding: 20px; }
                                    h1 { color: #E91E63; }
                                    .meta { color: #666; margin: 10px 0; }
                                  </style>
                                </head>
                                <body>
                                  <h1>${formatResultTitle(result)}</h1>
                                  <div class="meta">
                                    <p><strong>Court:</strong> ${formatCourt(result)}</p>
                                    <p><strong>Date:</strong> ${formatDate(result)}</p>
                                    <p><strong>ID:</strong> ${result.id}</p>
                                  </div>
                                  <div class="content">
                                    ${formatResultContent(result)}
                                  </div>
                                </body>
                              </html>
                            `;
                            const printWindow = window.open('', '_blank');
                            printWindow.document.write(printContent);
                            printWindow.document.close();
                            printWindow.print();
                          }}
                          title="Print"
                        >
                          <i className="bx bx-printer"></i>
                        </button>

                        <button
                          className="action-icon-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            const shareData = {
                              title: formatResultTitle(result),
                              text: `Check out this case: ${formatResultTitle(result)}`,
                              url: `${window.location.origin}/judgement/${result.id}`
                            };

                            if (navigator.share) {
                              navigator.share(shareData).catch((error) => console.log('Share cancelled'));
                            } else {
                              const textToCopy = `${shareData.title}\n${shareData.url}`;
                              navigator.clipboard.writeText(textToCopy).then(() => {
                                alert('Link copied to clipboard!');
                              }).catch(() => {
                                prompt('Copy this link:', shareData.url);
                              });
                            }
                          }}
                          title="Share"
                        >
                          <i className="bx bx-share-alt"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Bookmark Modal */}
      {showAddBookmarkModal && (
        <div
          className="modal-overlay-modern"
          onClick={() => setShowAddBookmarkModal(false)}
        >
          <div
            className="modal-container-modern"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-modern">
              <div className="modal-icon-modern">
                <i className="bx bx-bookmark-plus"></i>
              </div>
              <h3>Add to Bookmarks</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowAddBookmarkModal(false)}
              >
                <i className="bx bx-x"></i>
              </button>
            </div>
            <form onSubmit={handleAddBookmark}>
              <div className="modal-body-modern">
                <label className="modal-label">Bookmark Name</label>
                <input
                  type="text"
                  className="modal-input"
                  value={bookmarkName}
                  onChange={(e) => setBookmarkName(e.target.value)}
                  placeholder="Enter bookmark name..."
                  required
                />
              </div>
              <div className="modal-footer-modern">
                <button type="submit" className="modal-submit-btn">
                  <i className="bx bx-check"></i>
                  Add Bookmark
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        /* ============================================
           BASE LAYOUT
        ============================================ */
        .results-layout {
          min-height: 100vh;
          background: #fafafa;
        }

        .results-page-wrapper {
          padding-top: 140px;
          width: 100%;
        }

        /* ============================================
           HERO SECTION
        ============================================ */
        .results-hero {
          background: linear-gradient(135deg, #E91E63 0%, #E91E64 25%, #c41849 75%, #D81B60 100%);
          padding: 50px 20px 40px;
          text-align: center;
          color: white;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .hero-content-results {
          max-width: 800px;
          margin: 0 auto;
        }

        .search-type-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 50px;
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .search-type-badge i {
          font-size: 16px;
        }

        .results-hero-title {
          font-size: 2.2rem;
          font-weight: 800;
          margin: 0 0 10px 0;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          letter-spacing: -0.5px;
          line-height: 1.2;
        }

        .results-hero-subtitle {
          font-size: 1.05rem;
          opacity: 0.95;
          margin: 0;
          font-weight: 400;
        }

        /* ============================================
           MAIN CONTAINER
        ============================================ */
        .results-main-container {
          max-width: 1200px;
          margin: -30px auto 0;
          padding: 0 20px 40px;
          position: relative;
          z-index: 10;
        }

        /* ============================================
           FILTERS CARD
        ============================================ */
        .filters-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          margin-bottom: 30px;
        }

        .pagination-section {
          padding: 20px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #f0f0f0;
          gap: 20px;
          flex-wrap: wrap;
        }

        .pagination-info {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: #666;
          font-weight: 500;
        }

        .pagination-info i {
          font-size: 18px;
          color: #E91E63;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }

        .pagination-btn,
        .pagination-number {
          padding: 8px 14px;
          border: 2px solid #e8e8e8;
          background: white;
          color: #555;
          font-size: 14px;
          font-weight: 600;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .pagination-btn:hover,
        .pagination-number:hover {
          border-color: #E91E63;
          color: #E91E63;
          background: #fff9f0;
        }

        .pagination-number.active {
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
          border-color: transparent;
          box-shadow: 0 2px 8px rgba(245, 166, 35, 0.3);
        }

        .pagination-btn i {
          font-size: 16px;
        }

        .last-btn {
          margin-left: 6px;
        }

        /* Filters Section */
        .filters-section {
          padding: 20px 24px;
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.03), rgba(233, 30, 99, 0.02));
        }

        .search-refine-box {
          flex: 1;
          min-width: 250px;
          position: relative;
          display: flex;
          align-items: center;
        }

        .search-refine-box i {
          position: absolute;
          left: 14px;
          font-size: 18px;
          color: #999;
        }

        .refine-input {
          width: 100%;
          padding: 12px 40px 12px 44px;
          border: 2px solid #e8e8e8;
          border-radius: 10px;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .refine-input:focus {
          outline: none;
          border-color: #E91E63;
          box-shadow: 0 0 0 3px rgba(245, 166, 35, 0.1);
        }

        .clear-refine {
          position: absolute;
          right: 12px;
          width: 24px;
          height: 24px;
          border: none;
          background: #f0f0f0;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #666;
          transition: all 0.2s ease;
        }

        .clear-refine:hover {
          background: #e0e0e0;
          color: #333;
        }

        .refine-search-btn {
          padding: 12px 20px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .refine-search-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(233, 30, 99, 0.4);
        }

        .refine-search-btn i {
          font-size: 16px;
        }

        /* Dropdown Styles */
        .modern-dropdown-container,
        .filter-dropdown-wrapper {
          position: relative;
        }

        .modern-dropdown-btn {
          padding: 12px 16px;
          background: white;
          border: 2px solid #e8e8e8;
          border-radius: 10px;
          color: #555;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .modern-dropdown-btn:hover {
          border-color: #E91E63;
          color: #E91E63;
          background: #fff9f0;
        }

        .modern-dropdown-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .modern-dropdown-btn i:first-child {
          font-size: 18px;
        }

        .modern-dropdown-btn i.bx-chevron-down {
          font-size: 16px;
          transition: transform 0.3s ease;
        }

        .modern-dropdown-btn i.bx-chevron-down.rotated {
          transform: rotate(180deg);
        }

        .modern-dropdown-menu {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          background: white;
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
          min-width: 250px;
          max-height: 400px;
          overflow: hidden;
          z-index: 1001;
          border: 1px solid #f0f0f0;
        }

        .sort-menu {
          min-width: 200px;
        }

        .dropdown-search-box {
          padding: 12px;
          border-bottom: 1px solid #f0f0f0;
          background: #fafafa;
          position: relative;
        }

        .dropdown-search {
          width: 100%;
          padding: 10px 12px 10px 38px;
          border: 2px solid #e8e8e8;
          border-radius: 8px;
          font-size: 13px;
          transition: all 0.3s ease;
        }

        .dropdown-search:focus {
          outline: none;
          border-color: #E91E63;
          background: white;
        }

        .search-icon-dropdown {
          position: absolute;
          left: 24px;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
          font-size: 16px;
        }

        .dropdown-items-wrapper {
          max-height: 300px;
          overflow-y: auto;
          padding: 6px;
        }

        .dropdown-item-btn {
          width: 100%;
          padding: 10px 14px;
          border: none;
          background: white;
          text-align: left;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          color: #555;
          border-radius: 6px;
          margin-bottom: 4px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .dropdown-item-btn i {
          font-size: 16px;
          color: #E91E63;
        }

        .dropdown-item-btn:hover {
          background: linear-gradient(90deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
          color: #c41849;
        }

        .dropdown-item-btn.selected {
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
        }

        .dropdown-item-btn.selected i {
          color: white;
        }

        .no-items-found {
          padding: 12px;
          text-align: center;
          color: #999;
          font-size: 13px;
          font-style: italic;
        }

        .clear-filters-btn {
          padding: 12px 18px;
          background: white;
          border: 2px solid #c41849;
          color: #c41849;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .clear-filters-btn:hover {
          background: #c41849;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(233, 30, 99, 0.3);
        }

        .clear-filters-btn i {
          font-size: 16px;
        }

        /* ============================================
           RESULTS GRID
        ============================================ */
        .results-grid {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .no-results-found {
          background: white;
          border-radius: 16px;
          padding: 60px 20px;
          text-align: center;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
        }

        .no-results-found i {
          font-size: 60px;
          color: #ddd;
          margin-bottom: 20px;
        }

        .no-results-found h3 {
          font-size: 24px;
          color: #333;
          margin: 0 0 10px 0;
        }

        .no-results-found p {
          font-size: 15px;
          color: #666;
          margin: 0 0 24px 0;
        }

        .retry-btn {
          padding: 12px 24px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .retry-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(233, 30, 99, 0.4);
        }

        /* Result Cards */
        .result-card-modern {
          background: white;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .result-card-modern:hover {
          border-color: #E91E63;
          box-shadow: 0 8px 24px rgba(245, 166, 35, 0.15);
          transform: translateY(-4px);
        }

        .result-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
          gap: 12px;
        }

        .result-number {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.15), rgba(233, 30, 99, 0.1));
          color: #E91E63;
          font-weight: 700;
          font-size: 14px;
          border-radius: 8px;
        }

        .accuracy-badge-modern {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          background: linear-gradient(135deg, rgba(40, 167, 69, 0.1), rgba(34, 197, 94, 0.05));
          color: #28a745;
          font-size: 13px;
          font-weight: 600;
          border-radius: 50px;
        }

        .accuracy-badge-modern i {
          font-size: 16px;
        }

        .result-title-modern {
          font-size: 18px;
          font-weight: 700;
          color: #333;
          margin: 0 0 16px 0;
          cursor: pointer;
          transition: all 0.2s ease;
          line-height: 1.4;
        }

        .result-title-modern:hover {
          color: #E91E63;
        }

        .result-meta-modern {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 1px solid #f0f0f0;
        }

        .meta-item-modern {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #666;
          font-weight: 500;
        }

        .meta-item-modern i {
          font-size: 18px;
          color: #E91E63;
        }

        .citation-item {
          flex: 1 1 100%;
        }

        .result-content-modern {
          font-size: 14px;
          line-height: 1.7;
          color: #555;
          margin-bottom: 20px;
        }

        .result-content-modern mark {
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.2), rgba(233, 30, 99, 0.1));
          color: #E91E63;
          padding: 2px 6px;
          border-radius: 4px;
          font-weight: 600;
        }

        /* Actions */
        .result-actions-modern {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .action-primary-btn {
          padding: 12px 20px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
        }

        .action-primary-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(233, 30, 99, 0.4);
        }

        .action-primary-btn i {
          font-size: 18px;
        }

        .action-secondary-btns {
          display: flex;
          gap: 8px;
        }

        .action-icon-btn {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 2px solid #e8e8e8;
          background: white;
          color: #666;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .action-icon-btn:hover {
          border-color: #E91E63;
          color: #E91E63;
          background: #fff9f0;
          transform: translateY(-2px);
        }

        .action-icon-btn i {
          font-size: 18px;
        }

        /* ============================================
           MODAL
        ============================================ */
        .modal-overlay-modern {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
        }

        .modal-container-modern {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 500px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .modal-header-modern {
          padding: 24px;
          display: flex;
          align-items: center;
          gap: 14px;
          border-bottom: 1px solid #f0f0f0;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.05), rgba(233, 30, 99, 0.03));
        }

        .modal-icon-modern {
          width: 44px;
          height: 44px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 22px;
        }

        .modal-header-modern h3 {
          flex: 1;
          font-size: 18px;
          font-weight: 700;
          color: #333;
          margin: 0;
        }

        .modal-close-btn {
          width: 32px;
          height: 32px;
          border: none;
          background: #f0f0f0;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #666;
          transition: all 0.2s ease;
        }

        .modal-close-btn:hover {
          background: #e0e0e0;
          color: #333;
        }

        .modal-close-btn i {
          font-size: 20px;
        }

        .modal-body-modern {
          padding: 24px;
        }

        .modal-label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #333;
          margin-bottom: 8px;
        }

        .modal-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e8e8e8;
          border-radius: 10px;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .modal-input:focus {
          outline: none;
          border-color: #E91E63;
          box-shadow: 0 0 0 3px rgba(245, 166, 35, 0.1);
        }

        .modal-footer-modern {
          padding: 20px 24px;
          border-top: 1px solid #f0f0f0;
        }

        .modal-submit-btn {
          width: 100%;
          padding: 14px 20px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.3s ease;
        }

        .modal-submit-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(233, 30, 99, 0.4);
        }

        .modal-submit-btn i {
          font-size: 18px;
        }

        /* ============================================
           ANIMATIONS
        ============================================ */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
          }

        .bx-spin {
          animation: spin 1s linear infinite;
        }

        /* ============================================
           RESPONSIVE DESIGN
        ============================================ */
        @media (max-width: 768px) {
          .results-page-wrapper {
            padding-top: 130px;
          }

          .results-hero {
            padding: 35px 15px 30px;
          }

          .results-hero-title {
            font-size: 1.6rem;
          }

          .results-hero-subtitle {
            font-size: 0.95rem;
          }

          .results-main-container {
            margin-top: -20px;
            padding: 0 15px 30px;
          }

          .pagination-section {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
            padding: 16px 20px;
          }

          .pagination-controls {
            width: 100%;
            justify-content: center;
          }

          .filters-section {
            flex-direction: column;
            align-items: stretch;
            padding: 16px 20px;
            gap: 10px;
          }

          .search-refine-box {
            min-width: 100%;
          }

          .modern-dropdown-btn,
          .refine-search-btn,
          .clear-filters-btn {
            width: 100%;
            justify-content: center;
          }

          .result-card-modern {
            padding: 20px;
          }

          .result-title-modern {
            font-size: 16px;
          }

          .result-meta-modern {
            flex-direction: column;
            gap: 10px;
          }

          .citation-item {
            display: none;
          }

          .result-actions-modern {
            flex-direction: column;
          }

          .action-primary-btn {
            width: 100%;
            justify-content: center;
          }

          .action-secondary-btns {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 480px) {
          .results-hero-title {
            font-size: 1.4rem;
          }

          .search-type-badge {
            padding: 6px 14px;
            font-size: 12px;
          }

          .result-card-modern {
            padding: 16px;
          }

          .result-number {
            width: 32px;
            height: 32px;
            font-size: 13px;
          }

          .accuracy-badge-modern {
            font-size: 12px;
            padding: 5px 12px;
          }

          .result-title-modern {
            font-size: 15px;
          }

          .result-content-modern {
            font-size: 13px;
          }

          .pagination-btn,
          .pagination-number {
            padding: 6px 10px;
            font-size: 13px;
          }
        }
           /* ============================================
           REMOVE TOP WHITE SPACE
        ============================================ */
        body {
          margin: 0 !important;
          padding: 0 !important;
        }
      `}</style>
    </div>
  );
};

export default Results;